import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ServicesPaymentsPageStore } from './services-payments-page.store';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { FilterInput } from '@it2go/data-grid';
import { ServicePaymentGridItemObject } from '@it2go/types';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { billingPaymentTypes } from '../../../../../../../../types/src/services/enum/billing-payment-type.enum';
import { billingTransportTypes } from '../../../../../../../../types/src/services/enum/billing-transport-type.enum';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FilterBuilder } from '../../../helper/filter.builder';
import { insuranceTypeEnumOptions } from '../../../../../../../../types/src/common/enum/insurance-type.enum';
import { SwitchInputComponent } from '../../../../../components/kit/input/switch-input/switch-input.component';
import { CardTableComponent } from '../../../../shared/components/kit/table/card-table.component';
import { AsyncPipe } from '@angular/common';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Tooltip } from '@openng/optimus-ui/tooltip';

@Component({
    selector: 'app-services-payments-page',
    templateUrl: './services-payments-page.component.html',
    styleUrls: ['./services-payments-page.component.scss'],
    providers: [ServicesPaymentsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SwitchInputComponent,
        CardTableComponent,
        AsyncPipe,
        PrimeTemplate,
        ButtonComponent,
        TranslatePipe,
        Tooltip,
    ],
})
export class ServicesPaymentsPageComponent extends SubscriptionComponent implements OnInit {

    protected readonly store = inject(ServicesPaymentsPageStore);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly confirmBuilder = inject(ConfirmBuilder);

    serviceId: number | null = null;

    form = this.fb.group({
        showOutdated: this.fb.control<boolean>(false),
    });

    additionalFilters: FilterInput[][] = [];

    insuranceAbbrOptions: { name: string, value: string }[] = [];
    paymentTypeOptions = billingPaymentTypes.map((it) => (
        { name: 'billing.paymentType.' + it.name, value: it.name }
    ));
    transportTypeOptions = billingTransportTypes.map((it) => (
        { name: 'billing.transportType.' + it.name, value: it.name }
    ));

    columns: TableColumn[] = [
        { name: 'copy' },
        {
            name: 'insuranceAbbr',
            filterable: {
                type: 'select',
                selectOptions: () => this.insuranceAbbrOptions,
            },
        },
        {
            name: 'insuranceType',
            filterable: {
                type: 'select',
                selectOptions: () => insuranceTypeEnumOptions,
            },
        },
        // skryto na žádost Vojty
        // {
        //     name: 'paymentType',
        //     filterable: {
        //         type: 'select',
        //         selectOptions: () => this.paymentTypeOptions,
        //     },
        // },
        {
            name: 'transportType',
            filterable: {
                type: 'select',
                selectOptions: () => this.transportTypeOptions,
            },
        },
        {
            name: 'paymentForTransport',
            filterable: { type: 'number' },
        },
        {
            name: 'priceForPoint',
            filterable: { type: 'number' },
        },
        {
            name: 'pointsPerMinute',
            filterable: { type: 'number' },
        },
        {
            name: 'maximalMonthlyPrepaymentLimit',
            filterable: { type: 'number' },
        },
        {
            name: 'serviceICZ',
            filterable: { type: 'text' },
        },
        {
            name: 'electronicCommunication',
            type: TableColumnType.Bool,
        },
        {
            name: 'validFrom',
            type: TableColumnType.Date,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
        },
    ];

    constructor() {
        super();
        this.store.refreshGrid('getServicePayments', 'deleteServicePayment');
        this.store.registerSuccessMessage('deleteServicePayment', 'common.deleted');
        this.subs.push(
            this.store.routeParam.serviceId.subscribe((serviceId) => {
                if (serviceId) {
                    this.serviceId = Number(serviceId);
                    this.createAdditionalFilters(this.form.controls.showOutdated.value ?? false);
                }
            }),
            this.store.insuranceCompanies$.subscribe((insuranceCompany) => {
                this.insuranceAbbrOptions = insuranceCompany.map((it) => ({ name: it.code, value: it.code }));
            }),
            this.form.controls.showOutdated.valueChanges.subscribe((showOutdated) => {
                this.createAdditionalFilters(showOutdated ?? false);
            }),
        );
    }

    ngOnInit() {
        this.store.api.getInsuranceCompanies(FilterBuilder.singlePage({}));
    }

    create() {
        this.router.navigate([`./0`], { relativeTo: this.route });
    }

    detail(servicePayment: ServicePaymentGridItemObject) {
        this.router.navigate([`./${servicePayment.id}`], { relativeTo: this.route });
    }

    copy(servicePayment: ServicePaymentGridItemObject) {
        this.router.navigate([`./0`], {
            relativeTo: this.route,
            state: { copyId: servicePayment.id },
        });
    }

    delete(item: any) {
        this.confirmBuilder.confirm(
            'service.payments.deleteConfirmationHeader',
            'service.payments.deleteConfirmationLabel',
            () => {
                this.store.api.deleteServicePayment({ id: item.item.id });
            },
            { name: 'servicePaymentDeleteConfirm' + item.item.id },
            'pi-trash',
            item.event.target,
        );
    }

    private createAdditionalFilters(showOutdated: boolean) {
        if (this.serviceId) {
            const serviceIdFilter: FilterInput[][] = [
                [{ column: 'serviceId', operator: 'EQ', value: [this.serviceId.toString()] }],
            ];
            const dateFilters: FilterInput[][] = [
                [{ column: 'validFrom', operator: 'LTE', value: [new Date().toISOString()] }],
                [{ column: 'validTo', operator: 'GTE', value: [new Date().toISOString()] }],
            ];
            this.additionalFilters = showOutdated
                ? serviceIdFilter
                : [...serviceIdFilter, ...dateFilters];
        }

    }
}
